import cn from 'classnames';

import { PaymentTabs, PlanFamily, PlanKind } from 'src/types';
import { PaymentPageFooter } from 'src/pages/PaymentBetaPage/components/PaymentPageFooter';
import styles from './PaymentContainer.module.scss';
import { PlanSection } from '../PlanSection';
import {
  useGetUser,
  useBreakpoint,
  usePaymentControlState,
  useSubscriptionPlans,
  useCreditsAccountInfo,
} from 'src/hooks';
import {
  useFeatureFlagVariantKey,
  useFeatureFlagEnabled,
} from 'posthog-js/react';
import { StudentDiscountMessageBlock } from 'src/v2/components/StudentDiscountMessageBlock';
import { useMemo } from 'react';
import { PeriodSwitch } from 'src/pages/PaymentBetaPage/componentsV2/PeriodSwitch';
import { PlanTabs } from 'src/pages/PaymentBetaPage/componentsV2/PlanTabs';
import { OnDemandCreditPack } from 'src/pages/PaymentBetaPage/componentsV2/OnDemandCreditPack';
import { BannerDiscountMessageBlock } from 'src/v2/components/BannerDiscountMessageBlock/BannerDiscountMessageBlock';
import { PAYMENT_SUBSYSTEM_V3_VARIANT } from 'src/constants';

interface PaymentContainerProps {
  onOpenCheckout: () => void;
  onCloseCheckout: () => void;
  isShowUpdateToAnnually?: boolean;
  onChangeAppliedCoupon?: (value: string | undefined) => void;
}

export const PaymentContainer = ({
  onOpenCheckout,
  onCloseCheckout,
  isShowUpdateToAnnually,
  onChangeAppliedCoupon,
}: PaymentContainerProps) => {
  const { isSubscribedTier, isEducationUser, isFreeTier } = useGetUser();
  const { isDesktopAndUp } = useBreakpoint();
  const isEnabledOnDemandBanner = true;
  const paymentSubsystemVariant = useFeatureFlagVariantKey('payment-subsystem');
  const isShowNewPaymentModalV3 =
    paymentSubsystemVariant === PAYMENT_SUBSYSTEM_V3_VARIANT;
  const isThanksgivingCouponEnabled =
    useFeatureFlagEnabled('thanksgivingCoupon');

  // todo switch it temporary ON
  const isEnabledStandardPlan = true;
  const isEnabledUltraPlan = true;

  const { selectedPaymentTab, selectedPeriod } = usePaymentControlState();
  const { plans } = useSubscriptionPlans({});
  const { creditAccountInfo, isCreditAccountInfoLoading } =
    useCreditsAccountInfo();

  const isNoCredits = useMemo(
    () =>
      !isCreditAccountInfoLoading &&
      !creditAccountInfo?.reload_config?.auto_reload_enabled,
    [creditAccountInfo, isCreditAccountInfoLoading],
  );

  const excludedPlanKinds = useMemo(() => {
    const kinds: PlanKind[] = [];

    if (!isEnabledStandardPlan) {
      kinds.push(PlanKind.MAX_05_MONTHLY, PlanKind.MAX_05_ANNUALLY);
    }

    if (!isEnabledUltraPlan) {
      kinds.push(
        PlanKind.MAX_20_MONTHLY,
        PlanKind.MAX_20_ANNUALLY,
        PlanKind.BUSINESS_20_MONTHLY,
        PlanKind.BUSINESS_20_ANNUALLY,
      );
    }

    return kinds;
  }, [isEnabledStandardPlan, isEnabledUltraPlan]);

  const planList = useMemo(() => {
    const clearedPlans = (plans || [])
      ?.filter((item) => item.period === selectedPeriod)
      ?.filter(
        ({ plan_kind }) => !excludedPlanKinds.includes(plan_kind as PlanKind),
      );

    return selectedPaymentTab === PaymentTabs.INDIVIDUAL
      ? clearedPlans.filter((item) => item.plan_family === PlanFamily.MAX)
      : clearedPlans.filter((item) => item.plan_family === PlanFamily.BUSINESS);
  }, [plans, selectedPaymentTab, selectedPeriod, excludedPlanKinds]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        {isShowNewPaymentModalV3 ? (
          <div className={styles.titleWrapper}>
            <span className={styles.title}>
              {isSubscribedTier
                ? 'Unlock Advanced Features'
                : 'Upgrade to Continue'}
            </span>
            <span className={styles.description}>
              {isSubscribedTier
                ? 'Get increased limits and powerful features with higher tier.'
                : 'Keep building with SuperNinja and premium AI models.'}
            </span>
          </div>
        ) : (
          <h3 className={styles.title}>
            {isDesktopAndUp
              ? 'Subscription tailored for your needs'
              : 'Subscription Plans'}
          </h3>
        )}

        {isThanksgivingCouponEnabled && isFreeTier && (
          <BannerDiscountMessageBlock
            onChangeAppliedValue={onChangeAppliedCoupon}
          />
        )}

        {!isThanksgivingCouponEnabled && isEducationUser && (
          <StudentDiscountMessageBlock />
        )}

        <div className={styles.topSection}>
          <PlanTabs />
          {(!isSubscribedTier || isShowUpdateToAnnually) && (
            <PeriodSwitch onCloseCheckout={onCloseCheckout} />
          )}
        </div>
      </div>

      {/* TODO: WIP */}
      {/* {!!referralId && (
        <div className={styles.referralRow}>
          <Gift size={SVG_SIZE_M} />

          {selectedPeriod === CanonicalInterval.MONTHLY ? (
            <span className={styles.referralText}>
              Thank you for using a referral. Join Standard or Ultra membership
              today and enjoy your first month on us.
            </span>
          ) : (
            <span className={styles.referralText}>
              Thank you for using a referral! Join now and receive an additional
              discount equal to one month’s membership on the Standard or Ultra
              plan.
            </span>
          )}
        </div>
      )} */}

      <div
        className={cn(styles.planSectionWrapper, {
          [styles.withoutPeriodSelect]: isSubscribedTier,
        })}
      >
        {isEnabledOnDemandBanner && isSubscribedTier && isNoCredits && (
          <OnDemandCreditPack />
        )}
        <div className={styles.planWrapper}>
          {planList.map((item) => (
            <PlanSection
              key={item.plan_kind}
              sectionPlan={item}
              onOpenCheckout={onOpenCheckout}
            />
          ))}
        </div>

        <PaymentPageFooter />
      </div>
    </div>
  );
};
